import { Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import { amlzC2osobyDetailGet, amlzC2osobyDetailsend, amlzC2osobyGet } from 'services/spravaEmitenta/amlzData';
import { EStavZadostAmlz } from 'services/spravaEmitenta/historickeZadosti';
import { ISubjPravo, Iosoba } from 'services/spravaEmitenta/zadostPravo';

import getLanguageObject from 'utils/getLanguageObject';

import AgendaAmlz from './agendaAmlz/formular/AgendaAmlzFormular';

interface IOsobyAmlz {}

const OsobyAmlz: React.FC<IOsobyAmlz> = () => {
    const { language, profily, amlz } = useContext(Context);
    const [personData, setPersonData] = useState<ISubjPravo | undefined>();
    const [idOsoby, setIDosoby] = useState<number>(-1);
    const [loading, setLoading] = useState<boolean>(false);
    const [C2data, setC2Data] = useState<Iosoba[]>();
    const OsobyC2dataFiltered = C2data?.filter(
        e => e.stav !== EStavZadostAmlz.ODEBRAT && e.stav !== EStavZadostAmlz.PRIDAT
    );

    useEffect(() => {
        setLoading(true);
        amlzC2osobyGet(amlz?.idPoslednejHotovejZadosti!)
            .then(data => {
                setC2Data(data.osoby);
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getColumns = () => [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (_text: Iosoba, row: Iosoba) => (
                <p
                    style={{
                        margin: 0
                    }}
                >
                    {(row.jmeno || '') + ' ' + (row.prijmeni || '')}
                </p>
            )
        },
        {
            title: <TranslateText>EMAIL</TranslateText>,
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: <TranslateText>TELEFON</TranslateText>,
            dataIndex: 'telefon',
            key: 'telefon'
        },
        {
            title: <TranslateText>AUTENTIZACE</TranslateText>,
            dataIndex: 'autprvek',
            key: 'autprvek',
            render: (auth: string) => <TranslateText>{auth ? 'TYP_PRIHLASENI_' + auth : ''}</TranslateText>
        },
        {
            title: (
                <>
                    <TranslateText>STAV</TranslateText>
                </>
            ),
            dataIndex: 'stav',
            key: 'stav',
            render: (text: string, row: Iosoba) => (
                <>
                    <TranslateText>
                        {text
                            ? `STAVZADOSTI_SIMPLE_${text === EStavZadostAmlz.CDCPKEKONTR ? 'CDCPKEKONTR_ALT' : text}`
                            : ''}
                    </TranslateText>{' '}
                    {row.datum &&
                        (text === EStavZadostAmlz.UZIVCEKA || text === EStavZadostAmlz.UZIVDOPL) &&
                        `(${
                            text === EStavZadostAmlz.UZIVCEKA
                                ? getLanguageObject(language).ODESLANO + ' '
                                : text === EStavZadostAmlz.UZIVDOPL
                                  ? getLanguageObject(language).EMISE_PODZAD_STAV_DOPLENO + ' '
                                  : ''
                        }` +
                            dayjs(row.datum).format('DD.MM.YYYY HH:mm') +
                            ')'}
                </>
            )
        },
        {
            render: (_text: Iosoba, row: Iosoba, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'right'
                    }}
                >
                    {
                        <AnchorComponent
                            style={{
                                marginRight: 14
                            }}
                            id={`c2ZobrazitBtn${index}`}
                            key={`c2ZobrazitBtn${index}`}
                            onClick={() => {
                                setLoading(true);
                                setIDosoby(row.idosoby!);
                                amlzC2osobyDetailGet(amlz?.idPoslednejHotovejZadosti!, row.idosoby!)
                                    .then(resp => {
                                        if (row.stav === EStavZadostAmlz.INI) {
                                            const updateData = {
                                                ...resp,
                                                stav: EStavZadostAmlz.ROZP
                                            };
                                            row.id + '' === profily?.id + '' && delete updateData.doplneno;
                                            amlzC2osobyDetailsend(
                                                amlz?.idPoslednejHotovejZadosti!,
                                                row.idosoby!,
                                                updateData,
                                                'PUT'
                                            );
                                            setPersonData(updateData);
                                        } else {
                                            setPersonData(resp);
                                        }
                                    })
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>ZOBRAZIT</TranslateText>
                        </AnchorComponent>
                    }
                </div>
            )
        }
    ];

    return !personData ? (
        <LoadingWrapper loading={loading}>
            <Title level={4} text={'AMLZ_OSOBY_MENU'} />
            <Table
                loading={!C2data}
                rowKey={(_, index) => index || 0}
                /*  rowClassName={(_, i) =>
                        [...indexFailedFileValidation, ...indexFailedDateValidation].includes(i) ? 'tableRowRed' : ''
                    } */
                locale={{
                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                }}
                style={{
                    margin: '30px 0px'
                }}
                columns={getColumns()}
                dataSource={OsobyC2dataFiltered}
                pagination={false}
                rowClassName={row =>
                    row.stav === EStavZadostAmlz.HOTOVO ||
                    row.stav === EStavZadostAmlz.HOTOVOIIS ||
                    row.stav === EStavZadostAmlz.CDCPKONTR ||
                    row.stav === EStavZadostAmlz.AUTKONTROLA ||
                    row.stav === EStavZadostAmlz.CDCPKEKONTR
                        ? ''
                        : 'tableRowRed'
                }
            />

            <Paragraph
                style={{
                    display: 'block',
                    fontWeight: 600
                    //  color: '#ff4d4f'
                }}
                text="AMLZ_OSOBY_TEXT"
            />
        </LoadingWrapper>
    ) : personData ? (
        <LoadingWrapper loading={loading}>
            <AgendaAmlz
                setSubmitPersonData={(() => {}) as React.Dispatch<React.SetStateAction<ISubjPravo | undefined>>}
                personData={personData as ISubjPravo}
                idZadosti={amlz?.idPoslednejHotovejZadosti!}
                idOsoby={idOsoby}
                mode={'C2'}
                setPersonData={setPersonData}
                isAmlzRequired={idOsoby === Number(profily?.id)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                apiList={(() => {}) as any}
                setshowInstrukceAfterFinish={(() => {}) as React.Dispatch<React.SetStateAction<boolean>>}
                pdfCheckError={''}
                setPdfCheckError={() => {}}
            />
        </LoadingWrapper>
    ) : (
        <></>
    );
};

export default OsobyAmlz;
